import React, { useEffect } from "react";
import "./App.css";
import Home from "./modules/home";
import { BrowserRouter, useLocation } from "react-router-dom";
import { ErrorBoundary } from "react-error-boundary";
import { contacts, profile } from "./content/portfolio";
import portrait1280 from "./media/profile_pic-1280.webp";

const PROFILE_PATHS = new Set(["/", "/about", "/terminal"]);
export const SITE_ORIGIN = "https://lebed.dev";

export function getPageMetadata(pathname) {
  const name = `${profile.firstName} ${profile.lastName}`;
  const common = {
    name,
    description: profile.metaDescription,
    image: `${SITE_ORIGIN}/og-profile.png`,
    canonicalPath: "/",
    noindex: false,
  };
  if (pathname === "/about") {
    return {
      ...common,
      title: `About me | ${name}`,
      description: `${name}'s work experience, education, credentials and interests.`,
      canonicalPath: "/about",
    };
  }
  if (pathname === "/terminal") {
    return { ...common, title: `Interactive terminal | ${name}`, noindex: true };
  }
  if (!PROFILE_PATHS.has(pathname)) {
    return { ...common, title: `Page not found | ${name}`, description: "This page could not be found. Return to Nikolay Lebedinsky's profile.", canonicalPath: null, noindex: true };
  }
  return { ...common, title: `${name} | Backend developer` };
}

export function profileStructuredData(pathname) {
  if (!PROFILE_PATHS.has(pathname)) return null;
  const metadata = getPageMetadata(pathname);
  const image = new URL(portrait1280, SITE_ORIGIN).href;
  return {
    "@context": "https://schema.org",
    "@type": "ProfilePage",
    "@id": `${SITE_ORIGIN}${metadata.canonicalPath}#profile`,
    url: `${SITE_ORIGIN}${metadata.canonicalPath}`,
    name: metadata.title,
    primaryImageOfPage: image,
    mainEntity: {
      "@type": "Person",
      "@id": `${SITE_ORIGIN}/#person`,
      name: metadata.name,
      alternateName: "joxerx",
      image,
      url: `${SITE_ORIGIN}/`,
      description: profile.headline,
      sameAs: contacts.filter((contact) => contact.external).map((contact) => contact.href),
    },
  };
}

export function siteStructuredData(pathname) {
  if (pathname !== "/") return null;
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_ORIGIN}/#website`,
    name: getPageMetadata(pathname).name,
    alternateName: "lebed.dev",
    url: `${SITE_ORIGIN}/`,
  };
}

function NotFound() {
  return (
    <div className="dossier-page">
      <main className="dossier-section">
        <div className="dossier-section-heading">
          <p>404</p>
          <h1>Page not found</h1>
          <p>This page could not be found.</p>
          <a href="/">Return to Nikolay Lebedinsky's profile</a>
        </div>
      </main>
    </div>
  );
}

function usePageMetadata(pathname) {
  useEffect(() => {
    const metadata = getPageMetadata(pathname);
    document.title = metadata.title;
    const setMeta = (attribute, key, value) => {
      let node = document.head.querySelector(`meta[${attribute}="${key}"]`);
      if (!node) {
        node = document.createElement("meta");
        node.setAttribute(attribute, key);
        document.head.appendChild(node);
      }
      node.setAttribute("content", value);
    };
    setMeta("name", "description", metadata.description);
    setMeta("name", "robots", metadata.noindex ? "noindex, follow" : "index, follow");
    setMeta("property", "og:title", metadata.title);
    setMeta("property", "og:description", metadata.description);
    setMeta("property", "og:url", `${SITE_ORIGIN}${metadata.canonicalPath || "/"}`);
    setMeta("name", "twitter:title", metadata.title);
    setMeta("name", "twitter:description", metadata.description);
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (metadata.canonicalPath) {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = `${SITE_ORIGIN}${metadata.canonicalPath}`;
    } else canonical?.remove();
    for (const [attribute, structuredData] of [
      ["data-profile-schema", profileStructuredData(pathname)],
      ["data-site-schema", siteStructuredData(pathname)],
    ]) {
      let schema = document.head.querySelector(`script[${attribute}]`);
      if (structuredData) {
        if (!schema) {
          schema = document.createElement("script");
          schema.type = "application/ld+json";
          schema.setAttribute(attribute, "");
          document.head.appendChild(schema);
        }
        schema.textContent = JSON.stringify(structuredData);
      } else schema?.remove();
    }
  }, [pathname]);
}

export function AppRoutes() {
  const { pathname } = useLocation();
  usePageMetadata(pathname);
  return (
    <div className="App">
      <ErrorBoundary fallback={<span>Something went wrong.</span>}>
        {PROFILE_PATHS.has(pathname) ? <Home /> : <NotFound />}
      </ErrorBoundary>
    </div>
  );
}

function App({ Router = BrowserRouter, routerProps = {} }) {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }} {...routerProps}>
      <AppRoutes />
    </Router>
  );
}

export default App;
